import {
  ITranscriptElementFactory,
  ITextElement,
  IPunctElement,
} from './interfaces';

import { makeAutoObservable } from 'mobx';

/**
 * Creates and manages transcript elements
 * 
 * This factory is responsible for creating text and punctuation elements
 * with proper typing and cloning capabilities.
 */
export class TranscriptElementFactory implements ITranscriptElementFactory {
  // Instance methods that implement the interface
  createTextElement(
    id: string,
    value: string,
    startTime?: number,
    endTime?: number,
    confidence?: number,
    color?: string
  ): ITextElement {
    return TranscriptElementFactory.createTextElement(id, value, startTime, endTime, confidence, color);
  }

  createPunctElement(
    id: string,
    value: string,
    color?: string
  ): IPunctElement {
    return TranscriptElementFactory.createPunctElement(id, value, color);
  }
  /**
   * Creates a new text element
   * 
   * @param id Unique identifier for the element
   * @param value Text content of the element
   * @param startTime Optional start time in seconds
   * @param endTime Optional end time in seconds
   * @param confidence Optional confidence score (0-1)
   * @returns A new TextElement
   */
  static createTextElement(
    id: string,
    value: string,
    startTime?: number,
    endTime?: number,
    confidence?: number,
    color?: string
  ): ITextElement {
    // We create a plain object first so we can call makeAutoObservable on it
    const element: ITextElement = {
      id,
      type: 'text',
      value,
      speakerLabel: undefined,
      startTime,
      endTime,
      confidence,
      color,
      counter: 0,
      // Allow updating internal data references
      updateInternalData(segmentId?: string, paragraphId?: string, speakerLabel?: string): void {
        if (segmentId) this.segmentId = segmentId;
        if (paragraphId) this.paragraphId = paragraphId;
        if (speakerLabel) this.speakerLabel = speakerLabel;
      },

      // Implementation of clone method
      clone(newId?: string, preserveId: boolean = false): ITextElement {
        // If preserveId is true, keep the original ID
        const elementId = preserveId ? id : newId || `${id}-clone-${Date.now()}`;
        
        return TranscriptElementFactory.createTextElement(
          elementId,
          value,
          startTime,
          endTime,
          confidence,
          this.color
        );
      },

      // Serialize to JSON
      toJSON(): Record<string, any> {
        return {
          id,
          type: 'text',
          value,
          startTime: startTime ?? null,
          endTime: endTime ?? null,
          confidence: confidence ?? null,
          color: this.color ?? null,
        };
      },
      
      // Increment counter to trigger reactivity
      incrementCounter(): void {
        this.counter++;
      }
    };

    // Make the element observable so changes to .value etc. trigger MobX reactions
    makeAutoObservable(element);

    return element;
  }

  /**
   * Creates a new punctuation element
   * 
   * @param id Unique identifier for the element
   * @param value Punctuation content of the element
   * @returns A new PunctElement
   */
  static createPunctElement(
    id: string,
    value: string,
    color?: string
  ): IPunctElement {
    const element: IPunctElement = {
      id,
      type: 'punct',
      value,
      color,
      counter: 0,
      // Allow updating internal data references
      updateInternalData(segmentId?: string, paragraphId?: string, speakerLabel?: string): void {
        if (segmentId) this.segmentId = segmentId;
        if (paragraphId) this.paragraphId = paragraphId;
        if (speakerLabel) this.speakerLabel = speakerLabel;
      },

      // Implementation of clone method
      clone(newId?: string, preserveId: boolean = false): IPunctElement {
        // If preserveId is true, keep the original ID
        const elementId = preserveId ? id : newId || `${id}-clone-${Date.now()}`;
        
        return TranscriptElementFactory.createPunctElement(
          elementId,
          value,
          this.color
        );
      },

      // Serialize to JSON
      toJSON(): Record<string, any> {
        return {
          id,
          type: 'punct',
          value,
          color: this.color ?? null
        };
      },
      
      // Increment counter to trigger reactivity
      incrementCounter(): void {
        this.counter++;
      }
    };

    makeAutoObservable(element);

    return element;
  }

  /**
   * Creates an element from a JSON object
   * 
   * @param json JSON object with element data
   * @returns A new element
   */
  static fromJSON(json: any): ITextElement | IPunctElement {
    if (json.type === 'text') {
      return TranscriptElementFactory.createTextElement(
        json.id,
        json.value,
        json.startTime !== null ? json.startTime : undefined,
        json.endTime !== null ? json.endTime : undefined,
        json.confidence !== null ? json.confidence : undefined,
        json.color !== null ? json.color : undefined
      );
    } else {
      return TranscriptElementFactory.createPunctElement(
        json.id,
        json.value,
        json.color !== null ? json.color : undefined
      );
    }
  }
}